import { useContext } from 'react'
import {
  FormContext,
  FormContextProps,
  FormFieldsType,
  FormFieldType,
} from './Form.js'
import { FormField } from './FormField.js'
import { FormFieldGroup } from './FormFieldGroup.js'
import { TextInput } from '../TextInput/index.js'
import { TextAreaInput } from '../TextAreaInput/index.js'
import { SwitchInput } from '../SwitchInput/index.js'
import { CheckboxInput } from '../CheckboxInput/index.js'
import { NumberInput } from '../NumberInput/index.js'
import { DateInput } from '../DateInput/index.js'
import { SelectInput } from '../SelectInput/index.js'
import { RichTextEditor } from '../RichTextEditor/index.js'
import { getByPath, Path } from '@saulx/utils'
import { ReferenceInput } from '../ReferenceInput/index.js'
import { TagInput } from '../TagInput/index.js'
import { MultiSelectInput } from '../MultiSelectInput/index.js'
import { useClient } from '@based/sdk/react'
import { CodeInput } from '../CodeInput/index.js'

type FormFieldsProps = {}

const Field = ({ path, field, form, onRefOrderChange }: any) => {
  return (
    <FormField
      key={path.toString()}
      label={
        field.type! === 'reference'
          ? null
          : (field.label ??
            (() => {
              const lastPathElement = path[path.length - 1]
              if (
                typeof lastPathElement === 'object' &&
                lastPathElement !== null &&
                'en' in lastPathElement
              ) {
                return lastPathElement.en
              }
              return lastPathElement.toString()
            })())
      }
      description={
        ['checkbox', 'switch'].includes(field.type)
          ? undefined
          : field.description
      }
      error={getByPath(form.errors, path)}
      note={field.note}
    >
      {(() => {
        const disabled =
          field.disabled ||
          // form.isSubmitting ||
          form.isDisabledFromOutside
        const value = getByPath(form.values, path) ?? ''
        const errored = !!getByPath(form.errors, path)
        const onChange = (value: any) => form.setValue(path, value)

        switch (field.type) {
          case 'text':
            return (
              <TextInput
                disabled={disabled}
                value={value as string}
                onChange={onChange}
                error={errored}
                type={field.inputType}
                placeholder={field.placeholder}
              />
            )
          case 'textarea':
            return (
              <TextAreaInput
                disabled={disabled}
                value={value as string}
                onChange={onChange}
                error={errored}
                placeholder={field.placeholder}
              />
            )
          case 'switch':
            return (
              <SwitchInput
                disabled={disabled}
                value={value as boolean}
                onChange={onChange}
                description={field.description}
              />
            )
          case 'checkbox':
            return (
              <CheckboxInput
                disabled={disabled}
                value={value as boolean}
                onChange={onChange}
                error={errored}
                description={field.description}
              />
            )
          case 'number':
            return (
              <NumberInput
                disabled={disabled}
                value={value as number}
                onChange={onChange}
                error={errored}
                placeholder={field.placeholder}
              />
            )
          case 'date':
            return (
              <DateInput
                variant="date"
                disabled={disabled}
                value={value as number}
                onChange={onChange}
                error={errored}
                placeholder={field.placeholder}
              />
            )
          case 'datetime':
            return (
              <DateInput
                variant="date-time"
                disabled={disabled}
                value={value as number}
                onChange={onChange}
                error={errored}
                placeholder={field.placeholder}
              />
            )
          case 'select':
            return (
              <SelectInput
                disabled={disabled}
                filterable={field.filterable}
                options={field.options}
                value={value as string}
                onChange={onChange}
                placeholder={field.placeholder}
                error={errored}
              />
            )
          case 'richtext':
            return (
              <RichTextEditor
                value={value as string}
                onChange={onChange}
                disabled={disabled}
                error={errored}
                placeholder={field.placeholder}
              />
            )
          case 'json':
            return (
              <CodeInput
                language="json"
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
            )
          case 'reference':
            return (
              <ReferenceInput
                label={field.label}
                type={field.ref}
                variant={field.variant}
                limit={field?.limit}
                value={value}
                onChange={(value: any) => form.setValue(path, value)}
                error={errored}
                disabled={disabled}
                onOrderChange={
                  onRefOrderChange
                    ? (targetId, idx) => {
                        onRefOrderChange(path, targetId, idx)
                      }
                    : undefined
                }
              />
            )
          case 'tag':
            return (
              <TagInput
                disabled={disabled}
                variant={field.variant}
                options={field.options}
                value={value}
                onChange={(value: any) => form.setValue(path, value)}
                placeholder={field.placeholder}
                placeholderIcon={field.placeholderIcon}
                error={errored}
              />
            )
          case 'multiselect':
            return (
              <MultiSelectInput
                disabled={disabled}
                filterable={field.filterable}
                options={field.options}
                values={value as string[]}
                onChange={(values) => form.setValue(path, values)}
                placeholder={field.placeholder}
                error={errored}
              />
            )
          default: {
            console.error('Form.Fields: unknown field type')
            throw new Error('Form.Fields: unknown field type')
          }
        }
      })()}
    </FormField>
  )
}

const Fields = ({
  form,
  fields,
  path = [],
  onRefOrderChange,
}: {
  form: FormContextProps['form']
  fields: FormFieldsType | FormFieldType
  onRefOrderChange?: (path: string, draggedId: number, idx: number) => void
  path?: Path
}) => {
  return Object.entries(fields).map(([key, field]) => {
    const nestedPath = [...path, key]
    if ('type' in field) {
      return (
        <Field
          key={key}
          path={nestedPath}
          field={field}
          form={form}
          onRefOrderChange={onRefOrderChange}
        />
      )
    }

    return (
      <FormFieldGroup key={key} label={key} nested>
        <Fields
          path={nestedPath}
          fields={field}
          form={form}
          onRefOrderChange={onRefOrderChange}
        />
      </FormFieldGroup>
    )
  })
}

export function FormFields(_: FormFieldsProps) {
  const ctx = useContext(FormContext)

  if (!ctx) {
    throw new Error('missing form context')
  }

  const { fields, form, groups, activeGroup, onRefOrderChange } = ctx

  if (activeGroup) {
    const {
      label,
      description,
      fields: groupFields,
    } = groups.find((e) => e.label === activeGroup)

    const filteredFields: FormFieldsType = {}
    for (const field of groupFields) {
      filteredFields[field] = fields[field]
    }

    return (
      <FormFieldGroup label={label} description={description}>
        <Fields
          form={form}
          fields={filteredFields}
          onRefOrderChange={onRefOrderChange}
        />
      </FormFieldGroup>
    )
  }

  return (
    <FormFieldGroup>
      <Fields form={form} fields={fields} onRefOrderChange={onRefOrderChange} />
    </FormFieldGroup>
  )
}
